import type { LeadPosition, OfferDiscount, OfferDraftRow, OfferVatMode } from '../../../types';
import type { PaidPartialInvoice } from './finalInvoiceTotals';
import type { InvoiceRetention, InvoiceSiteLevy } from './invoicePayableTotals';
import { buildOfferMergedTableRows, type BuildOfferMergedTableRowsOptions } from './offerMergedTableRows';
import {
  applyCustomerFacingLaborPricing,
  type CustomerLaborPricingOptions,
} from './offerLaborPricing';

export type PartialInvoiceMode = 'percent' | 'amount' | 'position';

export type InvoiceLedgerSnapshotV1 = {
  v: 1;
  sourceDocumentTypeLabel: string;
  draftRows: OfferDraftRow[];
  discount: OfferDiscount | null;
  vat_mode: OfferVatMode;
  abschlagPercent: number;
  /** Current Abschlag net (EUR) when `partialInvoiceMode === 'amount'`. */
  partialInvoiceAmount?: number;
  partialInvoiceDescription: string;
  /** Custom title of the generated lump-sum partial-invoice line. */
  partialInvoiceLineName?: string;
  paidPartialInvoices: PaidPartialInvoice[];
  isPartialInvoice: boolean;
  isFinalInvoice: boolean;
  finalSnapshotLine?: OfferDraftRow | null;
  partialInvoiceMode?: PartialInvoiceMode;
  /** Prozent/Betrag kumuliert (bisherige Abschläge abgezogen); fehlt = Default je Modus. */
  partialInvoiceCumulative?: boolean;
  /** Per-row qty billed on this document (position mode). Keys from billingRowKey(). */
  positionInvoiceQuantities?: Record<string, number>;
  /** Optional Rechnungsvorlage used when this invoice was finalized (GoBD reproducibility). */
  layoutTemplateId?: string;
  layoutTemplateVersion?: number;
  siteLevy?: InvoiceSiteLevy | null;
  retention?: InvoiceRetention | null;
  siteLevyNet?: number;
  retentionGross?: number;
  priorRetentionsDueGross?: number;
  payableAmount?: number;
  skontoAmount?: number;
};

export function isLedgerSnapshotV1(x: unknown): x is InvoiceLedgerSnapshotV1 {
  if (typeof x !== 'object' || x === null) return false;
  const o = x as Record<string, unknown>;
  return o.v === 1 && typeof o.sourceDocumentTypeLabel === 'string';
}

function negateDraftRow(row: OfferDraftRow): OfferDraftRow {
  if (row.kind === 'title' || row.kind === 'text' || row.kind === 'image') return { ...row };
  return {
    ...row,
    quantity: row.quantity,
    unit_price_vk: -row.unit_price_vk,
  };
}

/** Export für Editor (manuelles Storno): Positionszeilen negieren. */
export function negateOfferDraftRowForStorno(row: OfferDraftRow): OfferDraftRow {
  return negateDraftRow(row);
}

/** Nach Bootstrap: verbleibende positive Einzelpreise auf Storno drehen (Legacy-Snapshots). */
export function forceStornoNegativeUnitPriceIfPositive(row: OfferDraftRow): OfferDraftRow {
  if (row.kind === 'title' || row.kind === 'text' || row.kind === 'image') return row;
  return row.unit_price_vk > 0 ? { ...row, unit_price_vk: -row.unit_price_vk } : row;
}

/**
 * Rechnungszeilen für `ledger_snapshot` wie in der Tabelle sichtbar (Merge Draft + API),
 * als persistierbare `freePosition`-Zeilen — damit Storno-Bootstrap nicht auf leeren draftRows steht.
 */
export function ledgerSnapshotDraftRowsFromEditor(
  positions: LeadPosition[],
  draftRows: OfferDraftRow[],
  options?: BuildOfferMergedTableRowsOptions,
  laborOpts?: CustomerLaborPricingOptions,
): OfferDraftRow[] {
  const merged = buildOfferMergedTableRows(positions, draftRows, options);
  const priced = laborOpts ? applyCustomerFacingLaborPricing(merged, laborOpts) : merged;
  const out: OfferDraftRow[] = [];
  for (const r of priced) {
    if (r.kind === 'title' || r.kind === 'text' || r.kind === 'image') continue;
    out.push({
      id: r.id.startsWith('api-') ? `ledger-${r.leadPositionId}` : r.id,
      kind: 'freePosition',
      leadPositionId: r.leadPositionId,
      sourceType: r.sourceType,
      quantity: r.quantity,
      unit: r.unit,
      item_name: r.item_name,
      description: r.description,
      internal_note: r.internal_note,
      unit_price_vk: r.unit_price_vk,
      unit_cost_ek: r.unit_cost_ek,
      surcharge_percent: r.surcharge_percent,
      // Effektiver Kundenpreis ist eingefroren — Minuten nicht noch einmal einrechnen.
      labor_minutes: 0,
      image: r.image,
      ...(r.costKind ? { costKind: r.costKind } : {}),
    });
  }
  return out;
}

function negateDiscount(d: OfferDiscount | null): OfferDiscount | null {
  if (!d || d.value <= 0) return d;
  if (d.type === 'absolute') return { ...d, value: -d.value };
  return { ...d };
}

function negatePaidPartials(rows: PaidPartialInvoice[]): PaidPartialInvoice[] {
  return rows.map((p) => ({
    ...p,
    netAmount: -p.netAmount,
    vatAmount: -p.vatAmount,
    grossAmount: -p.grossAmount,
    retentionGross: p.retentionGross != null ? -p.retentionGross : p.retentionGross,
  }));
}

export function negateLedgerSnapshot(snap: InvoiceLedgerSnapshotV1): InvoiceLedgerSnapshotV1 {
  let draftRows = (snap.draftRows ?? []).map(negateDraftRow);
  if (snap.isFinalInvoice && snap.finalSnapshotLine) {
    draftRows = [negateDraftRow(snap.finalSnapshotLine)];
  }
  const positionInvoiceQuantities =
    snap.positionInvoiceQuantities && typeof snap.positionInvoiceQuantities === 'object'
      ? Object.fromEntries(
          Object.entries(snap.positionInvoiceQuantities).map(([k, v]) => [k, -Number(v)]),
        )
      : undefined;
  return {
    ...snap,
    draftRows,
    discount: negateDiscount(snap.discount),
    paidPartialInvoices: negatePaidPartials(snap.paidPartialInvoices ?? []),
    finalSnapshotLine: snap.finalSnapshotLine ? negateDraftRow(snap.finalSnapshotLine) : null,
    positionInvoiceQuantities,
  };
}
